import styles from './CarouselHeading/CarouselHeading.scss';

import React from 'react';
import { Settings } from 'react-slick';

import { CarouselType } from 'src/api/contentful/contentTypes.cloverdotcom';

import { ArrowButton as CustomArrow, ArrowType } from '../ArrowButton/ArrowButton';

const BASE_SLIDER_SETTINGS: Readonly<Settings> = {
  speed: 500,
  cssEase: 'cubic-bezier(.23, 1, .32, 1)',
  infinite: false,
  slidesToShow: 1,
  slidesToScroll: 1,
  variableWidth: true,
  arrows: true,
  // default arrow type: CarouselType.FEATURE
  prevArrow: <CustomArrow arrowDirection={ArrowType.PREV} carouselType={CarouselType.FEATURE} />,
  nextArrow: <CustomArrow arrowDirection={ArrowType.NEXT} carouselType={CarouselType.FEATURE} />,
};

export const HEAD_ITEM_WIDTHS = {
  MOBILE: parseInt(styles.featureHeadItemWidthsMobile),
  TABLET: parseInt(styles.featureHeadItemWidthsTablet),
  DESKTOP: parseInt(styles.featureHeadItemWidthsDesktop),
  DESKTOP_BIG: parseInt(styles.featureHeadItemWidthsDesktopBig),
};

export function determineHeadItemWidth(sliderWidth: number, itemWidth: number) {
  if (sliderWidth < 700) return HEAD_ITEM_WIDTHS.MOBILE;
  if (sliderWidth < 1200) return HEAD_ITEM_WIDTHS.TABLET;
  if (itemWidth < 1600) return HEAD_ITEM_WIDTHS.DESKTOP;
  return HEAD_ITEM_WIDTHS.DESKTOP_BIG;
}

export const determineOverlap = (widthAllItems: number, sliderWidth: number, headItemWidth: number) => [
  widthAllItems - sliderWidth,

  // If we are not in mobile view
  // the headItem's width needs to be included as well
  sliderWidth > 700 ? headItemWidth : 0,

  // If we are in desktopBig (+1454px)
  // sliderWidth overlaps 100px more than it appears
  sliderWidth >= 1554 ? 100 : 0,
].reduce((sum, value) => sum + value, 0);

export const calculateSliderInfo = (sliderWidth: number, itemWidth: number, cardCount: number) => ({
  sliderWidth,
  itemWidth,
  overlap: determineOverlap(
    // width of all items
    itemWidth * cardCount,
    sliderWidth,
    determineHeadItemWidth(sliderWidth, itemWidth)
  ),
});

export function determineFeatureSettings(cardCount: number, slider: React.RefObject<HTMLDivElement>, firstItem: React.RefObject<HTMLAnchorElement>) {
  if (!slider || !slider.current || !firstItem || !firstItem.current) {
    return { ...BASE_SLIDER_SETTINGS };
  }
  const sliderWidth = Math.ceil(slider.current.getBoundingClientRect().width);
  const itemWidth = Math.ceil(firstItem.current.getBoundingClientRect().width);

  const sliderInfo = calculateSliderInfo(sliderWidth, itemWidth, cardCount);

  const isOverlapLessThanZero = sliderInfo.overlap < 0;
  const isWidthGreaterThan700 = sliderInfo.sliderWidth > 700;

  const numberOverlappedSlides = Math.ceil(sliderInfo.overlap / sliderInfo.itemWidth);

  return {
    // start with the old settings
    ...BASE_SLIDER_SETTINGS,

    // begin overwriting
    arrows: !isOverlapLessThanZero,

    // if overlap is less than zero => show all slides
    // else calculate how much it overlaps the slider and decide how many slides to show
    // add 1 to account for the headItem
    slidesToShow: isOverlapLessThanZero ? cardCount : cardCount - numberOverlappedSlides + (isWidthGreaterThan700 ? 1 : 0),

    // if there is no overlap, don't scroll
    // else if not mobile, scroll only 1
    slidesToScroll: isOverlapLessThanZero ? 0 : (isWidthGreaterThan700 ? numberOverlappedSlides : 1),
  };
}

export function computeSliderProps(
  type: CarouselType,
  cardCount: number,
  slider: React.RefObject<HTMLDivElement>,
  firstItem: React.RefObject<HTMLAnchorElement>,
  isMobile: boolean,
  isDesktop: boolean
): Readonly<Settings> {

  switch (type) {
    case (CarouselType.FEATURE):
      return determineFeatureSettings(cardCount, slider, firstItem);

    case (CarouselType.PRODUCT):
      return {
        ...BASE_SLIDER_SETTINGS,
        slidesToShow: isMobile ? 1 : isDesktop ? 3 : 2,
      };

    case (CarouselType.SQUARE):
      return {
        ...BASE_SLIDER_SETTINGS,
        infinite: true,
        centerMode: true,
        prevArrow: <CustomArrow arrowDirection={ArrowType.PREV} carouselType={CarouselType.SQUARE} />,
        nextArrow: <CustomArrow arrowDirection={ArrowType.NEXT} carouselType={CarouselType.SQUARE} infinite />,
      };

    case (CarouselType.HERO):
      return {
        ...BASE_SLIDER_SETTINGS,
        variableWidth: false,
        infinite: cardCount > 1,
        arrows: cardCount > 1,
        centerPadding: '0',
        centerMode: true,
        cssEase: 'linear',
        prevArrow: <CustomArrow arrowDirection={ArrowType.PREV} carouselType={CarouselType.HERO} />,
        nextArrow: <CustomArrow arrowDirection={ArrowType.NEXT} carouselType={CarouselType.HERO} infinite />,
      };
    default:
      return { ...BASE_SLIDER_SETTINGS };
  }
}
